import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Grid, Row, Col, Typography, Button } from '@sm/wds-react';
import { IconChevronDown, IconChevronUp } from '@sm/wds-icons';
import { t, T } from '@sm/intl';
import { TabletScreen } from '@sm/webassets';

import useIsomorphicLayoutEffect from '../../../helpers/useIsomorphicLayoutEffect';
import getClickHandlers from '../../../helpers/getClickHandlers';
import { getLinkGenerator } from '../../../helpers/logging';
import addScrollListener from '../helpers/scrollListener';
import COPY from '../helpers/copy';
import { MENU } from '../helpers/enums';
import MENU_CONFIG from '../helpers/menuConfig';
import logger from '../helpers/logger';
import mrxLogoDark from '../helpers/logo-marketresearch.svg';
import mrxLogoLight from '../helpers/logo-marketresearch--white.svg';
import ExpandableMenu from '../ExpandableMenu';
import './nav-for-ie.scss';

export const TEST_ID = 'MobileNavForIE';

const getLink = getLinkGenerator({
  ut_source: 'market_research',
  ut_source2: 'solutions',
  ut_source3: 'subnav',
});

const NavForTablet = ({ openMenu, setOpenMenu, isOpen, setIsOpen }) => {
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const navId = 'sm-mrx-nav-id-mobile';
  // Need to add this after layout is painted to fix flickering for MRX-21
  useIsomorphicLayoutEffect(() => {
    const removeScrollListener = addScrollListener(isAtTop => setScrolledToTop(isAtTop))();
    // This is because there is some element on IE 11 forcing the page to
    // be scroll-able to the right.
    document.body.classList.add('sm-nav-for-ie--hide-overflow-x');
    return () => {
      removeScrollListener();
      document.body.classList.remove('sm-nav-for-ie--hide-overflow-x');
    };
  });
  const useLight = scrolledToTop && !isOpen;
  const image = useLight ? mrxLogoLight : mrxLogoDark;
  const chevronColor = useLight ? 'light' : 'dark';

  return (
    <div className="sm-nav-for-ie__background-wrapper">
      <div
        className={classnames({
          'sm-nav-for-ie__wrapper': true,
          'sm-nav-for-ie__wrapper--scroll': !scrolledToTop,
          'sm-nav-for-ie__wrapper--top': scrolledToTop,
          'sm-nav-for-ie__wrapper--fullscreen': isOpen,
        })}
        id={navId}
      >
        <Grid noBleeds noGutters fluid data-testid={TEST_ID}>
          <Row
            className={classnames({
              'wds-m-b-5': isOpen,
              'sm-mrx-nav__title-row': true,
            })}
            role="button"
            tabIndex="0"
            aria-pressed={isOpen}
            {...getClickHandlers(() => setIsOpen(!isOpen))}
          >
            <Col className="sm-mrx-nav__logo-wrapper--mobile">
              <img src={image} alt={t(COPY.marketResearchSolutions)} className="sm-mrx-nav__logo" />
              <Typography variant="cardTitle" color={chevronColor}>
                <Choose>
                  <When condition={isOpen}>
                    <IconChevronUp />
                  </When>
                  <Otherwise>
                    <IconChevronDown />
                  </Otherwise>
                </Choose>
              </Typography>
            </Col>
          </Row>
          <If condition={isOpen}>
            <ExpandableMenu
              {...MENU_CONFIG.solutions}
              isOpen={openMenu === MENU.SOLUTIONS}
              onClick={() => setOpenMenu(MENU.SOLUTIONS)}
              variant="dark"
            />
            <ExpandableMenu {...MENU_CONFIG.services} variant="dark" />
            <ExpandableMenu
              {...MENU_CONFIG.moreResources}
              isOpen={openMenu === MENU.MORE_RESOURCES}
              onClick={() => setOpenMenu(MENU.MORE_RESOURCES)}
              variant="dark"
            />
            <Row className="sm-mrx-nav__cta">
              <TabletScreen>
                <Col xs={3} />
              </TabletScreen>
              <Col>
                <Button
                  color="upgrade"
                  href={getLink('/market-research/get-a-demo', {
                    ut_ctatext: 'Get a demo',
                  })}
                  {...getClickHandlers(() => {
                    logger.click.navigateTo('get_a_demo');
                  })}
                  className="sm-mrx-nav__cta-button--mobile"
                  stretched
                >
                  <T desc={COPY.getADemo} />
                </Button>
              </Col>
              <TabletScreen>
                <Col xs={3} />
              </TabletScreen>
            </Row>
          </If>
        </Grid>
      </div>
    </div>
  );
};

NavForTablet.propTypes = {
  openMenu: PropTypes.oneOf(Object.values(MENU)),
  setOpenMenu: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  scrollPosRef: PropTypes.shape({
    current: PropTypes.number,
  }),
  isScrolledToTop: PropTypes.bool,
};

NavForTablet.defaultProps = {
  openMenu: undefined,
  scrollPosRef: { current: 0 },
  isScrolledToTop: true,
};

export default NavForTablet;
